import { useState } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { updateDiveStatus } from '../../services/diveService'
import { useSessionStore } from '../../state'

export type DiveStatusControlsProps = {
  diveId: string
  hostId: string
  status: 'open' | 'full' | 'cancelled' | 'completed'
  isPast?: boolean
  onStatusChange?: (newStatus: string) => void
}

const DiveStatusControls = ({
  diveId,
  hostId,
  status,
  isPast,
  onStatusChange
}: DiveStatusControlsProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const user = useSessionStore(state => state.user)

  // Only show controls when the current user is the host
  if (!user || user.uid !== hostId) {
    return null
  }

  const handleMarkFull = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await updateDiveStatus(diveId, 'full', user.uid)
      onStatusChange?.('full')
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update status'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await updateDiveStatus(diveId, 'cancelled', user.uid)
      onStatusChange?.('cancelled')
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to cancel dive'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleMarkCompleted = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await updateDiveStatus(diveId, 'completed', user.uid)
      onStatusChange?.('completed')
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to complete dive'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const showMarkFull = status === 'open' && !isPast
  const showCancel = (status === 'open' || status === 'full') && !isPast
  const showMarkCompleted = isPast && (status === 'open' || status === 'full')

  if (!showMarkFull && !showCancel && !showMarkCompleted) {
    return null
  }

  return (
    <Stack spacing={1}>
      <Stack direction='row' spacing={1}>
        {showMarkFull && (
          <Button
            variant='outlined'
            onClick={handleMarkFull}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          >
            Mark as full
          </Button>
        )}
        {showCancel && (
          <Button
            variant='outlined'
            color='error'
            onClick={handleCancel}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          >
            Cancel dive
          </Button>
        )}
        {showMarkCompleted && (
          <Button
            variant='contained'
            color='success'
            onClick={handleMarkCompleted}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          >
            Mark as completed
          </Button>
        )}
      </Stack>
      {error && <Alert severity='error'>{error}</Alert>}
    </Stack>
  )
}

export default DiveStatusControls
